/**
 * BrandLogo — consumer component that renders the brand mark.
 *
 * Renders the core logo directly so auth/loading surfaces do not depend on
 * a global SVG symbol being mounted.
 *
 * Sizes:
 *   - hero:    420px (auth/setup hero)
 *   - auth:    320px (auth page secondary)
 *   - compact: 24px (nav/sidebar)
 *   - number:  arbitrary pixel size
 */
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import type { CSSProperties, FC } from 'react';

import * as styles from './brand.css';
import { STREAMLAIN_CORE_LOGO } from './streamlain-logo-mark';
import { StreamLainLogoMark } from './StreamLainLogoMark';

export type BrandLogoSize = 'hero' | 'auth' | 'compact' | number;

export interface BrandLogoProps {
  /** Pixel size or named preset. Default: compact (24px). */
  size?: BrandLogoSize;
  /** Play the continuous Flow-S loader animation. Loader surfaces only. */
  animate?: boolean;
  className?: string;
  style?: CSSProperties;
  'aria-label'?: string;
}

const SIZE_PRESETS: Record<Exclude<BrandLogoSize, number>, number> = {
  hero: 420,
  auth: 320,
  compact: 24,
};

function resolveSize(size: BrandLogoSize): number {
  return typeof size === 'number' ? size : SIZE_PRESETS[size];
}

export const BrandLogo: FC<BrandLogoProps> = ({
  size = 'compact',
  animate = false,
  className,
  style,
  'aria-label': ariaLabel = BRAND_DISPLAY_NAME,
}) => {
  const pixels = resolveSize(size);
  const composedClass = `${styles.mark} brand-logo ${className ?? ''}`.trim();

  return (
    <svg
      className={composedClass}
      width={pixels}
      height={pixels}
      viewBox={STREAMLAIN_CORE_LOGO.mark.viewBox}
      role="img"
      aria-label={ariaLabel}
      style={style}
      data-flow-animation={animate || undefined}
    >
      <StreamLainLogoMark animated={animate} />
    </svg>
  );
};
